import { Card, Heading, PageContainer, Text } from '@amelio/ui-library';

import {
    CommunicationBlock,
    type CommunicationBlockProps,
} from '../CommunicationBlock/CommunicationBlock';

import type { CommunicationBlockIdValue } from '../../communicationBlocks';
import type { ReactNode } from 'react';

import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import {
    CYCLE_CARD_SURFACE,
    CYCLE_CARD_SURFACE_BG_COLOR,
    CYCLE_CARD_SURFACE_BORDER_COLOR,
} from '@/pages/CycleEvaluation/components/cycleCardSurface';

export interface CommunicationBlockViewModel extends Omit<CommunicationBlockProps, 'zIndex'> {
    id: CommunicationBlockIdValue;
}

export interface CommunicationsProps {
    isLoading: boolean;
    blocks: CommunicationBlockViewModel[];
    /** Rendered as the Card's last child, so the step's own navigation controls land inside it. */
    footer?: ReactNode;
}

export const Communications = ({ isLoading, blocks, footer }: CommunicationsProps) => {
    const { t } = useAmelioTranslations();

    return (
        <PageContainer className={'cycle-evaluation-group-section'}>
            <Card
                padding={'none'}
                className={`${CYCLE_CARD_SURFACE} gap-6`}
                bgColor={CYCLE_CARD_SURFACE_BG_COLOR}
                borderColor={CYCLE_CARD_SURFACE_BORDER_COLOR}
            >
                <div>
                    <Heading level={4}>{t('cycleEvaluationCommunications.header.heading')}</Heading>

                    <Text className={'mt-3'}>
                        {t('cycleEvaluationCommunications.header.description')}
                    </Text>
                </div>

                {isLoading ? (
                    <Skeleton className={'mt-3 flex flex-col gap-4'}>
                        <Bone className={'h-28 w-full'} />

                        <Bone className={'h-28 w-full'} />

                        <Bone className={'h-28 w-full'} />

                        <Bone className={'h-28 w-full'} />
                    </Skeleton>
                ) : (
                    <div className={'mt-3 flex flex-col gap-4'}>
                        {blocks.map((block, index) => (
                            <CommunicationBlock
                                key={block.id}
                                title={block.title}
                                blockEnabled={block.blockEnabled}
                                onBlockEnabledChange={block.onBlockEnabledChange}
                                managerCardProps={block.managerCardProps}
                                employeeCardProps={block.employeeCardProps}
                                isReadOnly={block.isReadOnly}
                                // Descending z-index (same pattern as `MilestoneCard`) so an open popover always paints above the blocks below it.
                                zIndex={blocks.length - index}
                            />
                        ))}
                    </div>
                )}

                {footer}
            </Card>
        </PageContainer>
    );
};
